import { WIZARD_QUESTION_IDS } from '../../constants.js';
import { createSeededRng } from '../../domain/seededRandom.js';
import { detectOvernight } from '../../domain/schedule.js';
import { countRequiredBlankFields } from '../../domain/draftInvalidation.js';
import type {
  AiFillPatch,
  AnswerSource,
  ConnectedAnswers,
  LocationGrounding,
  LocationType,
  OperatingSchedule,
  RhythmAnswer,
  TimePeriod,
  WorldDraft,
} from '../../domain/types.js';
import { OPERATING_SCHEDULE_SCHEMA_VERSION, isNonBlankValue } from '../../domain/types.js';
import { applyDraftAnswer } from '../../domain/draftInvalidation.js';
import {
  CONNECTED_QUESTIONS,
  emptyConnectedAnswers,
  emptyLocationGrounding,
  questionOptions,
} from './questionSchema.js';

function pick<T>(items: readonly T[], seed: string, salt: string): T {
  const rng = createSeededRng(`${seed}:${salt}`);
  return items[Math.floor(rng() * items.length)]!;
}

export interface FallbackSuggestion {
  value: unknown;
  assumption: string;
  source: AnswerSource;
}

const GROUNDING_NAME_POOL = [
  'Harbor Cup',
  'Northside Books',
  'Microshaft Campus',
  'Cornell Commons',
  'BBC TV Station',
];

const GROUNDING_REGION_POOL = [
  'Vancouver, BC, Canada',
  'Toronto, ON, Canada',
  'London, England, UK',
  'Seattle, WA, USA',
];

const GROUNDING_LANGUAGE_POOL = ['English', 'French', 'Spanish', 'Japanese'];

const LOCATION_TYPE_POOL = [
  'Neighborhood café and community hub',
  'Software company campus with open-plan offices',
  'Public high school with arts focus',
  'Family home in a downtown condo tower',
];

const DESCRIPTION_POOL = [
  'A lived-in space with laptops, game consoles, and a busy kitchen.',
  'Open weekdays with regulars who know each other by name.',
  'A 30-story building with shared lounges and late-night study corners.',
];

export function suggestForQuestion(draft: WorldDraft, questionId: string): FallbackSuggestion {
  const opts = questionOptions(questionId);
  if (opts.length > 0) {
    const choice = pick(opts, draft.draftId, questionId);
    return {
      value: choice.value,
      assumption: `Suggested "${choice.label}" based on your location context.`,
      source: 'fallback',
    };
  }
  const textDefaults: Record<string, string> = {
    [WIZARD_QUESTION_IDS.connectedLocationType]: pick(LOCATION_TYPE_POOL, draft.draftId, questionId),
    [WIZARD_QUESTION_IDS.connectedDescription]: pick(DESCRIPTION_POOL, draft.draftId, questionId),
  };
  const text = textDefaults[questionId] ?? 'A typical day at this location.';
  return { value: text, assumption: `Suggested: "${text}"`, source: 'fallback' };
}

export function suggestLocationName(draftId: string): FallbackSuggestion {
  const name = pick(GROUNDING_NAME_POOL, draftId, 'location-name');
  return { value: name, assumption: `Suggested name: ${name}`, source: 'fallback' };
}

export function suggestGroundingField(
  draftId: string,
  field: 'name' | 'region' | 'language',
): FallbackSuggestion {
  const pools = {
    name: GROUNDING_NAME_POOL,
    region: GROUNDING_REGION_POOL,
    language: GROUNDING_LANGUAGE_POOL,
  };
  const value = pick(pools[field], draftId, `grounding-${field}`);
  return { value, assumption: `Suggested ${field}: ${value}`, source: 'fallback' };
}

export function suggestTimePeriod(draftId: string): TimePeriod {
  return {
    present: 'Present day',
    period: pick(['2020s', '1970s', '1920s'], draftId, 'period-label'),
    rationale: pick(
      ['Everyday life with familiar technology and social norms.', 'Contemporary setting for relatable drama.'],
      draftId,
      'period-rationale',
    ),
  };
}

export function defaultSchedule(_type: LocationType | null, _draftId: string): OperatingSchedule {
  const open = '08:00';
  const close = '20:00';
  return {
    schemaVersion: OPERATING_SCHEDULE_SCHEMA_VERSION,
    hasOperatingHours: true,
    timezoneStrategy: 'location',
    daysOpen: [1, 2, 3, 4, 5],
    openTime: open,
    closeTime: close,
    overnight: detectOvernight(open, close),
    closedBehavior: 'leave',
  };
}

/** Build deterministic AI-fill patches for blank fields on a step. */
export function buildAiFillPatchesForStep(
  draft: WorldDraft,
  step: 'location-grounding' | 'connected',
): AiFillPatch[] {
  const { blanks } = countRequiredBlankFields(draft);
  const patches: AiFillPatch[] = [];

  for (const blank of blanks) {
    if (step === 'location-grounding') {
      if (blank.fieldId === WIZARD_QUESTION_IDS.groundingName) {
        patches.push({
          questionId: blank.fieldId,
          value: suggestGroundingField(draft.draftId, 'name').value,
          source: 'ai_suggestion',
        });
      } else if (blank.fieldId === WIZARD_QUESTION_IDS.groundingRegion) {
        patches.push({
          questionId: blank.fieldId,
          value: suggestGroundingField(draft.draftId, 'region').value,
          source: 'ai_suggestion',
        });
      } else if (blank.fieldId === WIZARD_QUESTION_IDS.groundingLanguage) {
        patches.push({
          questionId: blank.fieldId,
          value: suggestGroundingField(draft.draftId, 'language').value,
          source: 'ai_suggestion',
        });
      }
    } else if (step === 'connected') {
      if (blank.fieldId === WIZARD_QUESTION_IDS.connectedLocationType) {
        patches.push({
          questionId: blank.fieldId,
          value: suggestForQuestion(draft, blank.fieldId).value,
          source: 'ai_suggestion',
        });
      } else if (blank.fieldId === WIZARD_QUESTION_IDS.connectedDescription) {
        patches.push({
          questionId: blank.fieldId,
          value: suggestForQuestion(draft, blank.fieldId).value,
          source: 'ai_suggestion',
        });
      } else if (blank.fieldId === WIZARD_QUESTION_IDS.connectedCrowd) {
        patches.push({
          questionId: blank.fieldId,
          value: { type: 'public_venue' },
          source: 'ai_suggestion',
        });
      } else if (blank.fieldId === WIZARD_QUESTION_IDS.connectedRhythm) {
        patches.push({
          questionId: blank.fieldId,
          value: defaultRhythmSuggestion(draft.draftId),
          source: 'ai_suggestion',
        });
      }
    }
  }
  if (step === 'connected') {
    const covered = new Set(patches.map((p) => p.questionId));
    if (!covered.has(WIZARD_QUESTION_IDS.connectedCrowd) && isCrowdUnanswered(draft)) {
      patches.push({
        questionId: WIZARD_QUESTION_IDS.connectedCrowd,
        value: { type: 'public_venue' },
        source: 'ai_suggestion',
      });
    }
    if (!covered.has(WIZARD_QUESTION_IDS.connectedRhythm) && isRhythmUnanswered(draft)) {
      patches.push({
        questionId: WIZARD_QUESTION_IDS.connectedRhythm,
        value: defaultRhythmSuggestion(draft.draftId),
        source: 'ai_suggestion',
      });
    }
  }
  return patches;
}

function isCrowdUnanswered(draft: WorldDraft): boolean {
  const ans = draft.answers[WIZARD_QUESTION_IDS.connectedCrowd];
  return !ans || ans.status === 'unanswered';
}

function isRhythmUnanswered(draft: WorldDraft): boolean {
  const ans = draft.answers[WIZARD_QUESTION_IDS.connectedRhythm];
  return !ans || ans.status === 'unanswered';
}

/** @deprecated v1 quick-start removed — returns draft unchanged. */
export function applyQuickStartAssumptions(draft: WorldDraft): WorldDraft {
  return draft;
}

/** @deprecated v1 quick-start removed — returns draft unchanged. */
export function applyModeAssumptions(draft: WorldDraft, _mode: unknown): WorldDraft {
  return draft;
}

/** @deprecated v1 per-type grounding — use suggestGroundingField. */
export function suggestLocationGrounding(
  _type: LocationType,
  draftId: string,
): Partial<LocationGrounding> {
  return {
    name: String(suggestLocationName(draftId).value),
    region: String(suggestGroundingField(draftId, 'region').value),
    language: String(suggestGroundingField(draftId, 'language').value),
  };
}

/** Fill blank connected answers with local fallbacks (guided flow helper). */
export function fillBlankConnectedAnswers(draft: WorldDraft): WorldDraft {
  let next = draft;
  const connected = draft.connected ?? emptyConnectedAnswers();
  for (const q of CONNECTED_QUESTIONS) {
    const existing = next.answers[q.questionId];
    if (existing && existing.status !== 'unanswered' && existing.status !== 'stale') continue;
    const fb = suggestForQuestion(next, q.questionId);
    const result = applyDraftAnswer(next, {
      questionId: q.questionId,
      value: fb.value,
      status: 'suggested',
      source: fb.source,
      dependencies: q.dependencies,
    });
    if (result.ok) next = result.draft;
  }
  if (!isNonBlankValue(connected.locationType) && next.connected) {
    next = { ...next, connected: next.connected };
  }
  return next;
}

export function defaultConnectedAnswers(draftId: string): ConnectedAnswers {
  return {
    locationType: String(suggestForQuestion({ ...emptyDraft(draftId), connected: null }, WIZARD_QUESTION_IDS.connectedLocationType).value),
    description: String(suggestForQuestion({ ...emptyDraft(draftId), connected: null }, WIZARD_QUESTION_IDS.connectedDescription).value),
    crowd: { type: 'public_venue' },
    rhythm: { skipped: true },
  };
}

function emptyDraft(draftId: string): WorldDraft {
  return {
    schemaVersion: '2.0.0',
    draftId,
    createdAt: '',
    updatedAt: '',
    wizardStep: 'connected',
    location: emptyLocationGrounding(),
    connected: null,
    answers: {},
    timePeriod: null,
    schedule: null,
    characterDrafts: [],
    reviewAccepted: false,
  };
}

export function defaultRhythmSuggestion(_draftId: string): RhythmAnswer {
  return { skipped: false, type: 'steady_busy_all_day' };
}
